// src/utils/personaDetection.js
// Utility functions for detecting user persona and determining default mode/layout

/**
 * User Personas:
 * - new_user: No clubs joined, needs to discover
 * - player: Member of clubs, primarily interested in events/assignments
 * - creator: Has created one or more clubs
 * - manager: Has managerial role in clubs (Owner, Manager, Admin)
 */

export const PERSONAS = {
  NEW_USER: 'new_user',
  PLAYER: 'player',
  COACH: 'coach',
  CREATOR: 'creator',
  MANAGER: 'manager'
};

export const MODES = {
  EXPLORE: 'explore',
  DASHBOARD: 'dashboard'
};

export const DASHBOARD_VARIANTS = {
  PLAYER: 'player',
  COACH: 'coach',   // Coaches get a tactical dashboard
  CREATOR: 'creator',
  OWNER: 'owner'    // Club owners/managers get a specialized dashboard
};

/**
 * Detect the user's primary persona based on their clubs and roles
 * @param {Object} user - Firebase user object
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {string} One of PERSONAS values
 */
export const detectUserPersona = (user, userClubs) => {
  // Not logged in or no user - treat as new user for discovery
  if (!user) {
    return PERSONAS.NEW_USER;
  }

  // No clubs joined
  if (!userClubs || userClubs.length === 0) {
    return PERSONAS.NEW_USER;
  }

  // Check for creator/manager roles
  const hasCreatorRole = userClubs.some(membership => 
    membership.role && 
    ['creator', 'Creator'].includes(membership.role)
  );

  const hasManagerRole = userClubs.some(membership => 
    membership.role && 
    ['owner', 'Owner', 'manager', 'Manager', 'admin', 'Admin'].includes(membership.role)
  );

  if (hasCreatorRole) {
    return PERSONAS.CREATOR;
  }

  if (hasManagerRole) {
    return PERSONAS.MANAGER;
  }

  // Default to player if they have clubs but no management roles
  return PERSONAS.PLAYER;
};

/**
 * Get the default mode for a given persona
 * @param {string} persona - One of PERSONAS values
 * @returns {string} One of MODES values
 */
export const getDefaultMode = (persona) => {
  switch (persona) {
    case PERSONAS.NEW_USER:
      return MODES.EXPLORE;
    case PERSONAS.PLAYER:
    case PERSONAS.CREATOR:
    case PERSONAS.MANAGER:
      return MODES.DASHBOARD;
    default:
      return MODES.EXPLORE;
  }
};

/**
 * Get the dashboard variant for a given persona
 * @param {string} persona - One of PERSONAS values
 * @returns {string} One of DASHBOARD_VARIANTS values
 */
export const getDashboardVariant = (persona) => {
  switch (persona) {
    case PERSONAS.CREATOR:
    case PERSONAS.MANAGER:
      return DASHBOARD_VARIANTS.OWNER; // Use specialized Club Owner Dashboard
    case PERSONAS.COACH:
      return DASHBOARD_VARIANTS.COACH; // Use Coach Dashboard
    case PERSONAS.PLAYER:
    case PERSONAS.NEW_USER:
    default:
      return DASHBOARD_VARIANTS.PLAYER;
  }
};

/**
 * Check if a role is a coaching role
 * @param {string} role - The role to check
 * @returns {boolean}
 */
export const isCoachRole = (role) => {
  if (!role) return false;
  const coachRoles = [
    'HeadCoach', 'headCoach', 'head_coach',
    'AssistantCoach', 'assistantCoach', 'assistant_coach',
    'Coach', 'coach',
    'GoalkeeperCoach', 'goalkeeperCoach', 'goalkeeper_coach',
    'FitnessCoach', 'fitnessCoach', 'fitness_coach'
  ];
  return coachRoles.includes(role);
};

/**
 * Check if user has coaching roles in any programs
 * @param {Array} enrolledPrograms - Array of user's enrolled programs with roles
 * @returns {boolean}
 */
export const hasCoachingRole = (enrolledPrograms) => {
  if (!enrolledPrograms || enrolledPrograms.length === 0) return false;
  return enrolledPrograms.some(p => isCoachRole(p.userRole));
};

/**
 * Get programs where user is a coach
 * @param {Array} enrolledPrograms - Array of user's enrolled programs with roles
 * @returns {Array} Programs where user has a coaching role
 */
export const getCoachingPrograms = (enrolledPrograms) => {
  if (!enrolledPrograms || enrolledPrograms.length === 0) return [];
  return enrolledPrograms.filter(p => isCoachRole(p.userRole));
};

/**
 * Get user's managed clubs (clubs where they are creator, owner, or manager)
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {Array} Array of club objects
 */
export const getManagedClubs = (userClubs) => {
  if (!userClubs || userClubs.length === 0) return [];

  const managerRoles = ['creator', 'Creator', 'owner', 'Owner', 'manager', 'Manager', 'admin', 'Admin'];
  
  return userClubs
    .filter(membership => 
      membership.role && 
      managerRoles.includes(membership.role) &&
      membership.club
    )
    .map(membership => ({
      ...membership.club,
      userRole: membership.role
    }));
};

/**
 * Get user's member clubs (clubs where they are just a member, not manager)
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {Array} Array of club objects
 */
export const getMemberClubs = (userClubs) => {
  if (!userClubs || userClubs.length === 0) return [];

  const managerRoles = ['creator', 'Creator', 'owner', 'Owner', 'manager', 'Manager', 'admin', 'Admin'];
  
  return userClubs
    .filter(membership => 
      membership.club &&
      (!membership.role || !managerRoles.includes(membership.role))
    )
    .map(membership => ({
      ...membership.club,
      userRole: membership.role || 'Member'
    }));
};

/**
 * Check if user can manage a specific club
 * @param {string} clubId - The club ID to check
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {boolean}
 */
export const canManageClub = (clubId, userClubs) => {
  if (!clubId || !userClubs || userClubs.length === 0) return false;

  const managerRoles = ['creator', 'Creator', 'owner', 'Owner', 'manager', 'Manager', 'admin', 'Admin'];
  
  return userClubs.some(membership => {
    const membershipClubId = membership.club?._id || membership.club;
    return String(membershipClubId) === String(clubId) && 
           membership.role && 
           managerRoles.includes(membership.role);
  });
};

/**
 * Get user's role in a specific club
 * @param {string} clubId - The club ID to check
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {string|null} The role or null if not a member
 */
export const getUserClubRole = (clubId, userClubs) => {
  if (!clubId || !userClubs || userClubs.length === 0) return null;

  const membership = userClubs.find(m => {
    const membershipClubId = m.club?._id || m.club;
    return String(membershipClubId) === String(clubId);
  });

  return membership?.role || null;
};

/**
 * Get user statistics for dashboard quick stats
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {Object} Stats object
 */
export const getUserStats = (userClubs) => {
  if (!userClubs || userClubs.length === 0) {
    return {
      totalClubs: 0,
      managedClubs: 0,
      memberClubs: 0,
      creatorClubs: 0
    };
  }

  const managerRoles = ['creator', 'Creator', 'owner', 'Owner', 'manager', 'Manager', 'admin', 'Admin'];
  const creatorRoles = ['creator', 'Creator'];

  const managedClubs = userClubs.filter(m => 
    m.role && managerRoles.includes(m.role)
  ).length;

  const creatorClubs = userClubs.filter(m => 
    m.role && creatorRoles.includes(m.role)
  ).length;

  return {
    totalClubs: userClubs.length,
    managedClubs,
    memberClubs: userClubs.length - managedClubs,
    creatorClubs
  };
};

/**
 * Determine if user should see the welcome/onboarding flow
 * @param {Object} user - Firebase user object
 * @param {Object} userProfile - Backend user profile
 * @param {Array} userClubs - Array of user's club memberships
 * @returns {boolean}
 */
export const shouldShowWelcome = (user, userProfile, userClubs) => {
  // Not logged in
  if (!user) return false;

  // New user with no clubs
  if (!userClubs || userClubs.length === 0) {
    return true;
  }

  // User profile indicates new user
  if (userProfile?.isNewUser) {
    return true;
  }

  return false;
};

/**
 * Get persona-based empty state message
 * @param {string} persona - One of PERSONAS values
 * @param {string} section - The section (e.g., 'events', 'assignments', 'clubs')
 * @returns {Object} { icon, title, message, actionLabel, actionTarget }
 */
export const getEmptyStateMessage = (persona, section) => {
  const messages = {
    [PERSONAS.NEW_USER]: {
      clubs: {
        icon: '🏆',
        title: 'Welcome to Sport Loop!',
        message: 'Discover clubs and programs near you to get started.',
        actionLabel: 'Explore Nearby',
        actionTarget: 'explore'
      },
      events: {
        icon: '📅',
        title: 'No Upcoming Events',
        message: 'Join a program to see your upcoming events here.',
        actionLabel: 'Find Programs',
        actionTarget: 'explore'
      },
      assignments: {
        icon: '📝',
        title: 'No Assignments',
        message: 'Join a program to receive assignments.',
        actionLabel: 'Find Programs',
        actionTarget: 'explore'
      }
    },
    [PERSONAS.PLAYER]: {
      events: {
        icon: '📅',
        title: 'No Upcoming Events',
        message: 'Your enrolled programs don\'t have any upcoming events.',
        actionLabel: 'Browse Programs',
        actionTarget: 'explore'
      },
      assignments: {
        icon: '📝',
        title: 'All Caught Up!',
        message: 'You don\'t have any pending assignments.',
        actionLabel: null,
        actionTarget: null
      }
    },
    [PERSONAS.CREATOR]: {
      managedClubs: {
        icon: '🏢',
        title: 'Create Your First Club',
        message: 'Start your sports organization journey!',
        actionLabel: 'Create Club',
        actionTarget: 'createClub'
      }
    }
  };

  return messages[persona]?.[section] || {
    icon: '📭',
    title: 'Nothing Here Yet',
    message: 'Check back later.',
    actionLabel: null,
    actionTarget: null
  };
};










